import React from 'react';
import { Typography, RegularButton } from 'fronton-react';
import { MagnifyingGlassIcon } from '@fronton/icons-react';
import { cx } from 'linaria';
import { NewSuggestion } from 'types/suggestions';
import { capitalize } from 'utils/capitalize';
import { useTranslation } from 'react-i18next';
import { useSuggestionsButtonAction } from '../../hooks/use-suggestions-button-action';
import {
  suggestion,
  suggestionButton,
  suggestionText,
  suggestionCaption,
  suggestionContentWrap,
  suggestionTextContentWrap,
  servicesSuggestionIcon,
} from './suggestion-list-item-styles';
import { SuggestionListItemNewImage } from './suggestion-list-item-new-image';
import { getCaption } from './utils';
import { SuggestionListItemServicesIcon } from './suggestion-list-item-services-icon';
import { SuggestionListItemOption } from './suggestion-list-item-option';

const DATA_QA_NEW_SUGGESTION_BUTTON = 'new-suggestion-button';

interface SuggestionListItemNewProps {
  newSuggestion: NewSuggestion;
  elementIndex: number;
  navigationIndex: number;
  isSrp: boolean;
  suggestionSelector: string;
  buttonAction: ReturnType<typeof useSuggestionsButtonAction>;
}

export const SuggestionListItemNew: React.FC<SuggestionListItemNewProps> = ({
  newSuggestion,
  elementIndex,
  navigationIndex,
  isSrp,
  buttonAction,
  suggestionSelector,
}) => {
  const { t } = useTranslation();
  const { pageUrl, name, labels, imageUrl } = newSuggestion;

  const buttonParams = {
    ...buttonAction(
      elementIndex,
      {
        block: t('header.searchbar.categories'),
        index: elementIndex,
        text: name,
      },
      capitalize(name),
      pageUrl,
    ),
  };

  const caption = getCaption(labels);

  const isService = ['SDP', 'SLP'].includes(newSuggestion.type);

  const imageFallback = isService ? (
    <SuggestionListItemServicesIcon />
  ) : (
    <MagnifyingGlassIcon type="outline" size="l" color="text-minor" />
  );

  return (
    <SuggestionListItemOption elementIndex={elementIndex} navigationIndex={navigationIndex}>
      <RegularButton
        variant="pseudo"
        tabIndex={-1}
        className={cx(
          suggestionSelector,
          suggestion,
          elementIndex === navigationIndex && 'active',
          isSrp && suggestionButton,
        )}
        key={name + labels?.[0]}
        data-qa={DATA_QA_NEW_SUGGESTION_BUTTON}
        {...buttonParams}
        href={pageUrl || ''}
      >
        <div className={suggestionContentWrap}>
          <SuggestionListItemNewImage
            src={imageUrl}
            alt={capitalize(name)}
            fallback={imageFallback}
            fallbackClassName={isService ? servicesSuggestionIcon : undefined}
          />
          <div className={suggestionTextContentWrap}>
            <Typography variant="m" size="body_short" className={suggestionText} color="text-primary">
              {capitalize(name)}
            </Typography>
            {caption && (
              <Typography variant="caption" color="text-secondary" className={suggestionCaption}>
                {caption}
              </Typography>
            )}
          </div>
        </div>
      </RegularButton>
    </SuggestionListItemOption>
  );
};
